import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ExternalNode } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/external_node_pb";
import { NetworkMemberType } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";

import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import ApplicationPageLayout, {
  useApplicationPageLayoutMode,
} from "components/ApplicationShell/ApplicationPageLayout";
import { Button } from "components/Graphs/Button";

import { FullScreenLoadingSpinner } from "components/FullScreenLoader";

import { useNodeDetail } from "components/NodeDetail/BrevConnect/useNodeDetail";
import { getSSHPortId } from "components/NodeDetail/Environment/TabContent/Access/SSHAccess/addSSHAccessToEnvironmentRequest";
import { ConnectDevice } from "./ConnectDevice";
import {
  resetBrevConnectScrollPosition,
  toAddedComputeNode,
} from "./ConnectDevice/utils";
import type {
  AddedComputeNode,
  BrevConnectRouteParams,
  ComputeNodeNetworkMode,
  Phase,
} from "./ConnectDevice/utils";
import DeviceConnected from "./DeviceConnected";

const BackButton: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <button
    type="button"
    onClick={onBack}
    className="flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-sm text-secondary transition-colors duration-150 hover:text-white"
  >
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path
        d="M8.5 3l-3 3.5 3 3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    Back to Compute
  </button>
);

const PageTopBar: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div className="mb-7 flex flex-wrap items-center gap-[18px]">
    <BackButton onBack={onBack} />
  </div>
);

const SuccessCheckIcon = () => (
  <span className="inline-flex shrink-0 animate-check-pop">
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle
        cx="11"
        cy="11"
        r="10"
        className="fill-highlight/12 stroke-highlight"
        strokeWidth="1.4"
      />
      <path
        d="M6.5 11l3 3 6-5.5"
        className="stroke-highlight animate-check-draw"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="20"
        strokeDashoffset="20"
      />
    </svg>
  </span>
);

const HeaderActions: React.FC<{
  onBack: () => void;
  onAddAnother: () => void;
}> = ({ onBack, onAddAnother }) => (
  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 max-[1023px]:w-full max-[1023px]:[order:-1] max-[1023px]:flex-nowrap max-[1023px]:justify-end">
    <Button
      type="button"
      variant="secondary"
      onClick={onAddAnother}
      className="shrink-0 gap-1.5 bg-zinc-800 hover:bg-zinc-700"
    >
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <path
          d="M5.5 1.5v8M1.5 5.5h8"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>
      Add another device
    </Button>
    <Button
      type="button"
      variant="secondary"
      onClick={onBack}
      className="shrink-0"
    >
      Done
    </Button>
  </div>
);

const PageHeader: React.FC<{
  connected: boolean;
  node?: AddedComputeNode;
  onBack: () => void;
  onAddAnother: () => void;
}> = ({ connected, node, onBack, onAddAnother }) => (
  <div
    className={`${connected ? "mb-6" : "mb-10"} transition-[margin] duration-[400ms] ease-in-out`}
  >
    <div
      className={`flex flex-wrap items-start justify-between gap-6 ${
        connected
          ? "max-[1023px]:flex-col max-[1023px]:items-stretch max-[1023px]:gap-[14px]"
          : ""
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5">
          {connected ? <SuccessCheckIcon /> : null}
          <h1
            className={`m-0 text-[22px] font-semibold leading-[1.3] transition-colors duration-[400ms] ${
              connected ? "text-highlight" : "text-zinc-200"
            }`}
          >
            {connected && node
              ? `${node.name} was successfully added`
              : "Brev Connect"}
          </h1>
        </div>
        <p className="mt-1.5 text-sm leading-[1.6] text-zinc-400">
          {connected
            ? `Your ${node?.deviceModel ?? "device"} joined the mesh network.`
            : "Brev Connect lets you add any Linux machine to Brev so you can connect over SSH, grant access to teammates, open ports, and share HTTP services."}
        </p>
      </div>
      {connected ? (
        <HeaderActions onBack={onBack} onAddAnother={onAddAnother} />
      ) : null}
    </div>
  </div>
);

const NodeDetailError: React.FC<{
  onRetry: () => void;
}> = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
    <h2 className="m-0 text-base font-semibold text-zinc-200">
      Could not load local device
    </h2>
    <Button type="button" variant="secondary" onClick={onRetry}>
      Retry
    </Button>
  </div>
);

const BrevConnectPage: React.FC<{
  phase: Phase;
  registeredNode?: ExternalNode | null;
  nodeDetailError?: unknown;
  onRetryNodeDetail: () => Promise<void>;
  onBack: () => void;
  onRegistered: (node: ExternalNode) => void;
  onCheckSSHAccess: () => Promise<void>;
  onGrantSSHAccess: (userId: string, linuxUser: string) => Promise<void>;
  onRevokeSSHAccess: (userId: string, linuxUser: string) => Promise<void>;
  onSetNetworkMode: (mode: ComputeNodeNetworkMode) => Promise<void>;
  onAddAnother: () => void;
}> = ({
  phase,
  registeredNode,
  nodeDetailError,
  onRetryNodeDetail,
  onBack,
  onRegistered,
  onCheckSSHAccess,
  onGrantSSHAccess,
  onRevokeSSHAccess,
  onSetNetworkMode,
  onAddAnother,
}) => {
  const isApplicationShellRedesign = useApplicationPageLayoutMode() === "fluid";
  const [copied, setCopied] = useState(false);
  const [everCopied, setEverCopied] = useState(false);
  const [registrationToken] = useState(() => crypto.randomUUID());
  const copiedTimerRef = useRef<number | null>(null);
  const connected = phase === "success";
  const selectedNode = registeredNode
    ? toAddedComputeNode(registeredNode)
    : undefined;
  const networkMode =
    registeredNode?.networkMemberType === NetworkMemberType.SHARED
      ? "meshed"
      : "isolated";
  useEffect(() => {
    document.title = connected
      ? "Local compute added | Brev"
      : "Add local compute | Brev";
  }, [connected]);

  useEffect(() => {
    if (nodeDetailError) {
      console.error(nodeDetailError);
    }
  }, [nodeDetailError]);

  useLayoutEffect(() => {
    resetBrevConnectScrollPosition();

    const animationFrame = window.requestAnimationFrame(
      resetBrevConnectScrollPosition,
    );

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [phase]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current !== null) {
        window.clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const handleCopy = async (command: string) => {
    try {
      await navigator.clipboard.writeText(command);
    } catch {
      return;
    }

    setCopied(true);

    if (copiedTimerRef.current !== null) {
      window.clearTimeout(copiedTimerRef.current);
    }
    copiedTimerRef.current = window.setTimeout(() => {
      setCopied(false);
    }, 2000);

    setEverCopied(true);
  };

  if (connected && !selectedNode) {
    if (nodeDetailError) {
      return (
        <div className="w-full px-4 pb-20">
          <PageTopBar onBack={onBack} />
          <NodeDetailError onRetry={onRetryNodeDetail} />
        </div>
      );
    }

    return <FullScreenLoadingSpinner />;
  }

  return (
    <>
      <div className="flex min-h-[calc(100vh-96px)] flex-col items-center pb-20 font-sans text-zinc-200 antialiased [&_button]:!font-[inherit] [&_input]:!font-[inherit] [&_select]:!font-[inherit] [&_textarea]:!font-[inherit] [&_button:focus-visible]:!outline [&_button:focus-visible]:!outline-2 [&_button:focus-visible]:!outline-highlight [&_button:focus-visible]:!outline-offset-2 [&_select:focus-visible]:!outline [&_select:focus-visible]:!outline-2 [&_select:focus-visible]:!outline-highlight [&_select:focus-visible]:!outline-offset-2 [&_textarea:focus-visible]:!outline [&_textarea:focus-visible]:!outline-2 [&_textarea:focus-visible]:!outline-highlight [&_textarea:focus-visible]:!outline-offset-2 [&_input::placeholder]:!text-zinc-500 [&_textarea::placeholder]:!text-zinc-500">
        <div
          className={`w-full max-[720px]:px-[20px] ${
            isApplicationShellRedesign ? "max-w-[100rem] px-12" : "mx-auto px-4"
          }`}
        >
          <PageTopBar onBack={onBack} />
          <PageHeader
            connected={connected}
            node={selectedNode}
            onBack={onBack}
            onAddAnother={onAddAnother}
          />
          {!connected ? (
            <ConnectDevice
              copied={copied}
              everCopied={everCopied}
              registrationToken={registrationToken}
              onCopy={handleCopy}
              onRegistered={onRegistered}
            />
          ) : (
            <DeviceConnected
              node={selectedNode!}
              sshAccess={registeredNode?.sshAccess ?? []}
              isSSHEnabled={Boolean(getSSHPortId(registeredNode?.ports ?? []))}
              usesCertAuthSSHProvider={
                registeredNode?.labels.sshprovider === "certauth"
              }
              onCheckSSHAccess={onCheckSSHAccess}
              onGrantUser={onGrantSSHAccess}
              onRevokeUser={onRevokeSSHAccess}
              networkMode={networkMode}
              onNetworkModeChange={onSetNetworkMode}
            />
          )}
        </div>
      </div>
    </>
  );
};

export const BrevConnectRoute: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const isApplicationShellRedesign = useApplicationPageLayoutMode() === "fluid";
  const { orgId } = useParams<BrevConnectRouteParams>();
  const phase: Phase = location.pathname.endsWith("/brev-connect/registered")
    ? "success"
    : "waiting";
  const registeredNodeId = new URLSearchParams(location.search).get("nodeId");
  const {
    node: registeredNode,
    fetchError: nodeDetailError,
    refetch: refetchRegisteredNode,
    grantSSHAccess,
    revokeSSHAccess,
    setNetworkMemberType,
  } = useNodeDetail(phase === "success" ? (registeredNodeId ?? "") : "");
  const newPath = `/org/${orgId}/brev-connect/new`;
  const registeredPath = `/org/${orgId}/brev-connect/registered`;
  const environmentsPath = `/org/${orgId}/environments`;

  if (phase === "success" && !registeredNodeId) {
    return <Redirect to={newPath} />;
  }

  return (
    <ApplicationPageLayout
      className={
        isApplicationShellRedesign
          ? "max-w-none py-0 sm:px-0 lg:px-0"
          : "py-10 sm:px-6 lg:px-8"
      }
    >
      <BrevConnectPage
        key={phase}
        phase={phase}
        registeredNode={registeredNode}
        nodeDetailError={nodeDetailError}
        onRetryNodeDetail={refetchRegisteredNode}
        onBack={() => history.push(environmentsPath)}
        onRegistered={(node) =>
          history.push(
            `${registeredPath}?nodeId=${encodeURIComponent(node.externalNodeId)}`,
          )
        }
        onCheckSSHAccess={refetchRegisteredNode}
        onGrantSSHAccess={grantSSHAccess}
        onRevokeSSHAccess={revokeSSHAccess}
        onSetNetworkMode={(mode) =>
          setNetworkMemberType(
            mode === "meshed"
              ? NetworkMemberType.SHARED
              : NetworkMemberType.ISOLATED,
          )
        }
        onAddAnother={() => history.push(newPath)}
      />
    </ApplicationPageLayout>
  );
};

export default BrevConnectPage;
